import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { savePassage } from "@/lib/passageCache";
import { ChevronDown, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

// Cache the available translations across the session (fetched once from /config).
let _cache = null;
async function loadTranslations() {
  if (_cache) return _cache;
  const { data } = await api.get("/config");
  _cache = (data.translations || []).filter((t) => t.available);
  return _cache;
}

// Inline Bible-version selector shown on the study page wherever the current translation
// is displayed. Switching instantly swaps the displayed Scripture text into the chosen
// version (the AI teaching stays as-is). Renders as a static label if no onPassageChange
// handler is provided (e.g. public shared views).
export default function TranslationPicker({ session, onPassageChange }) {
  const passage = session.passage || {};
  const [translations, setTranslations] = useState(_cache || []);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const current = (passage.translation || "niv").toLowerCase();
  const currentName = passage.translation_name || current.toUpperCase();
  const ref = session.study?.passage_reference || passage.reference;

  useEffect(() => {
    loadTranslations().then(setTranslations).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!onPassageChange) {
    return <span>{currentName}</span>;
  }

  const pick = async (t) => {
    setOpen(false);
    if (t.id === current || !ref) return;
    setBusy(true);
    try {
      const { data } = await api.get("/bible/passage", {
        params: { reference: ref, translation: t.id },
      });
      if (data.ok && (data.verses || []).length) {
        const newPassage = {
          ...passage,
          verses: data.verses,
          translation: (data.translation || t.id).toLowerCase(),
          translation_name: data.translation_name || t.name,
          copyright: data.copyright,
          reference: data.reference || ref,
          requested_translation: t.id,
        };
        onPassageChange(newPassage);
        savePassage(ref, t.id, data);
        // Persist the chosen version onto the study so it survives a reload (item: version reset).
        if (session.id) {
          api.put(`/study/sessions/${session.id}/translation`, { translation: newPassage.translation }).catch(() => {});
        }
      } else {
        toast.error("Couldn't load that version. Try another.");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load that version.");
    }
    setBusy(false);
  };

  return (
    <span className="relative inline-block">
      <button
        type="button"
        data-testid="study-translation-picker"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 uppercase tracking-widest hover:text-[#00E5FF] transition-colors"
      >
        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
        <span>{currentName}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-10" onClick={() => setOpen(false)} tabIndex={-1} />
          <span
            data-testid="translation-menu"
            className="absolute left-0 top-6 z-20 block w-60 bg-[#0E131F] border border-[#1E273D] rounded-xl shadow-lg p-1.5 normal-case tracking-normal"
          >
            {translations.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`translation-opt-${t.id}`}
                onClick={() => pick(t)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 transition-colors ${
                  t.id === current ? "bg-[#10222A] text-[#00E5FF]" : "text-slate-400 hover:bg-[#0B0F16]"
                }`}
              >
                <span className="min-w-0">
                  <span className="font-medium">{t.short}</span>{" "}
                  <span className="text-slate-500 text-xs">{t.name}</span>
                </span>
                {t.id === current && <Check className="w-3.5 h-3.5 shrink-0" />}
              </button>
            ))}
          </span>
        </>
      )}
    </span>
  );
}
